export function isArchiveEvent(eventType) {
  return eventType === "Archived" || eventType === "Unarchived";
}

function getItemDate(item) {
  return item?.event_date ? new Date(item.event_date).getTime() : 0;
}

function getRequestGroupKey(item) {
  if (item?.document_request_id) {
    return `req-${item.document_request_id}`;
  }
  if (item?.milestone === "Released") {
    return `release-${item.sponsor_name}-${item.event_date}`;
  }
  return `request-${item.sponsor_name}-${item.event_date}-${item.status}`;
}

function buildRequestGroups(requestItems) {
  const groups = new Map();

  for (const item of requestItems) {
    const key = getRequestGroupKey(item);
    if (!groups.has(key)) {
      groups.set(key, {
        type: "request",
        sponsor_name: item.sponsor_name || "Unknown sponsor",
        document_request_id: item.document_request_id || null,
        events: [],
        sortDate: getItemDate(item),
      });
    }

    const group = groups.get(key);
    group.events.push(item);
    group.sortDate = Math.max(group.sortDate, getItemDate(item));
  }

  const requestGroups = Array.from(groups.values());

  const sortedByRequestedDate = [...requestGroups].sort(
    (a, b) => getRequestedDate(a) - getRequestedDate(b)
  );
  const initialGroup = sortedByRequestedDate[0];
  const initialRequestKey = initialGroup
    ? initialGroup.document_request_id || initialGroup.sponsor_name
    : null;

  return requestGroups.map((group) => {
    const requestedEvent = group.events.find(
      (event) => event.milestone === "Requested" || event.status !== "Released"
    );
    const releasedEvent = group.events.find(
      (event) => event.milestone === "Released" || event.status === "Released"
    );
    const groupKey = group.document_request_id || group.sponsor_name;
    const isInitialRequest = initialRequestKey === groupKey;

    return {
      ...group,
      requestedEvent,
      releasedEvent,
      statusBadge: releasedEvent ? "Released" : requestedEvent?.status || "Pending",
      isInitialRequest,
    };
  });
}

function getRequestedDate(group) {
  const requestedEvent = group.events.find(
    (event) => event.milestone === "Requested" || event.status !== "Released"
  );
  return getItemDate(requestedEvent || group.events[0]);
}

export function buildFallbackArchiveSection(fallbackArchive) {
  if (!fallbackArchive?.event_type || !isArchiveEvent(fallbackArchive.event_type)) {
    return null;
  }

  const eventDate = getItemDate(fallbackArchive);
  return {
    type: "archive",
    item: fallbackArchive,
    // Without a date, keep archive at the top (docs cannot be requested after archive).
    sortDate: eventDate || Number.MAX_SAFE_INTEGER,
  };
}

function hasArchiveSection(sections) {
  return sections.some((section) => section.type === "archive");
}

export function buildActivityLogSections(items = [], fallbackArchive = null) {
  const requestItems = items.filter((item) => item?.event_type === "Request");
  const archiveItems = items.filter((item) => isArchiveEvent(item?.event_type));

  const requestSections = buildRequestGroups(requestItems);
  const archiveSections = archiveItems.map((item) => ({
    type: "archive",
    item,
    sortDate: getItemDate(item),
  }));

  const sections = [...requestSections, ...archiveSections];

  const fallbackSection = buildFallbackArchiveSection(fallbackArchive);
  if (fallbackSection && !hasArchiveSection(sections)) {
    sections.push(fallbackSection);
  }

  return sections.sort((a, b) => b.sortDate - a.sortDate);
}
